import React, { useMemo } from 'react';
import { styled as styled2 } from '@compiled/react';
import noop from 'lodash/noop';
import { GROUP_BY_STATUS } from '@atlassian/jira-business-constants';
import {
	statusCategoryForId,
	StatusCategoryIds,
} from '@atlassian/jira-common-constants/src/status-categories';
import { useIntl } from '@atlassian/jira-intl';
import type { ColumnId } from '@atlassian/jira-platform-board-kit';
import { ColumnTransitionZonesContainer } from '@atlassian/jira-platform-board-kit-legacy-dnd';
import { useCardTransitionsOld } from '../../../../controllers/card-transitions-old';
import { useJWMBoardFeatures } from '../../../../controllers/features-context';
import { useGroupBy } from '../../../../controllers/group-by';
import {
	useColumnByStatusId,
	useHasMultipleTransitionsBetweenStatuses,
} from '../../../../services/board-store';
import messages from './messages';
import { NoTransitionsAvailableMessageContainer } from './styled';

export const TransitionZones = ({ columnId }: { columnId: ColumnId }) => {
	const { formatMessage } = useIntl();
	const { sourceColumn, cardDragState, transitions, onCardDragOver } =
		useCardTransitionsOld(columnId);
	const [hasMultipleTransitions] = useHasMultipleTransitionsBetweenStatuses();
	const sourceId = Number(cardDragState.issue?.statusId);
	const { featureView } = useJWMBoardFeatures();
	const [thisColumn] = useColumnByStatusId({ statusId: Number(columnId) });
	const [groupBy] = useGroupBy();
	const isGroupedByStatus = groupBy === GROUP_BY_STATUS;

	return useMemo(() => {
		if (
			!thisColumn ||
			!sourceColumn ||
			!isGroupedByStatus ||
			!cardDragState.issue ||
			thisColumn.isCollapsed
		) {
			return null;
		}

		const sourceStatus = {
			id: sourceId,
			name: sourceColumn.name,
		};

		const transitionZonesData = transitions.map((transition) => ({
			transitionId: Number(transition.transitionId),
			transitionName: transition.name,
			targetStatusId: Number(transition.toStatusId),
			targetStatusName: thisColumn.name,
			targetStatusCategory: statusCategoryForId(
				// @ts-expect-error TS7053: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Readonly<{ undefined: 1; new: 2; done: 3; indeterminate: 4; }>'.
				StatusCategoryIds[thisColumn.statusCategory],
			),
			isAvailable: true,
			isGlobal: transition.isGlobal,
		}));

		if (hasMultipleTransitions) {
			if (transitionZonesData.length > 0) {
				return (
					<ColumnTransitionZonesContainer
						hasSwimlanes={false}
						columnId={columnId}
						sourceColumnId={sourceId}
						sourceStatus={sourceStatus}
						transitions={transitionZonesData}
						hoverIntent={cardDragState.hoverIntent}
						columnDraggingOver={cardDragState.columnId}
						notAvailableMessage={messages.notAvailableMessage}
						onCardDragOver={onCardDragOver(columnId)}
						onTransitionSelected={noop}
					/>
				);
			}

			if (sourceId !== columnId) {
				return (
					<Wrapper>
						<NoTransitionsAvailableMessageContainer featureView={featureView}>
							{formatMessage(messages.transitionUnavailableMessage)}
						</NoTransitionsAvailableMessageContainer>
					</Wrapper>
				);
			}
		}

		return null;
	}, [
		cardDragState.columnId,
		cardDragState.hoverIntent,
		cardDragState.issue,
		columnId,
		featureView,
		formatMessage,
		hasMultipleTransitions,
		isGroupedByStatus,
		onCardDragOver,
		sourceColumn,
		sourceId,
		thisColumn,
		transitions,
	]);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled2.div({
	position: 'absolute',
	top: 0,
	height: '100%',
});
